import {sendEvent} from '@github-ui/hydro-analytics'
import {reportIpmAction, sendIpmMetric, type IpmMetricTags} from '@github-ui/ipm-telemetry'
import type {ProductAnnouncementDataWithBanner} from '@github-ui/product-announcement-shared/components/ProductAnnouncement.types'
import {TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, ButtonGroup, IconButton, Label, LinkButton} from '@primer/react'
import type {CSSProperties, ReactNode} from 'react'
import {useEffect, useMemo} from 'react'

import styles from './PromotionalBanner.module.css'
import {safeHref} from './safe-href'
import {IpmBannerBase} from '../ipm-banner-base/IpmBannerBase'

export interface PromotionalBannerAnalytics {
  category: string
  label: string
  location: string
}

export interface PromotionalBannerAction {
  id: string
  label: string
  href: string
  analyticsAction: string
  /** Campaign-defined name for this action, reported as the `variant` telemetry tag. */
  variant?: string
  /** Set for destinations off github.com; same-site actions navigate in place. */
  openInNewTab?: boolean
}

export interface PromotionalBannerActionMenu {
  ariaLabel: string
  analyticsAction: string
  variant?: string
  items: PromotionalBannerAction[]
}

export interface PromotionalBannerPayload {
  campaign: string
  title: string
  description: string
  dismissPath: string
  /** Canonical priority-registry ID. Older payloads omit it and fall back to the analytics category. */
  nudgeId?: string
  analytics: PromotionalBannerAnalytics
  primaryAction: Omit<PromotionalBannerAction, 'href'> & {href?: string}
  announcement?: {version: 1; data: ProductAnnouncementDataWithBanner}
  testSelector: string
  label?: string
  actionMenu?: PromotionalBannerActionMenu
}

export interface PromotionalBannerTelemetry {
  component: string
  variant: string
}

export interface PromotionalBannerProps {
  banner: PromotionalBannerPayload
  rounded?: boolean
  /** Campaign-owned action for behavior that cannot be represented by a link. */
  customPrimaryAction?: (telemetryTags: IpmMetricTags) => ReactNode
  /** Applied to the banner surface alongside the shared styles, for campaign artwork. */
  surfaceClassName?: string
  /** Custom properties the campaign's artwork rules read. */
  surfaceStyle?: CSSProperties
  /** Leading visual for a menu item, chosen by the campaign. */
  actionVisual?: (action: PromotionalBannerAction) => ReactNode
  telemetry?: PromotionalBannerTelemetry
}

const DEFAULT_TELEMETRY: PromotionalBannerTelemetry = {
  component: 'PromotionalBanner',
  variant: 'promotion',
}

function analyticsLabel(analytics: PromotionalBannerAnalytics) {
  return `${analytics.label}location:${analytics.location};`
}

function analyticsEvent(analytics: PromotionalBannerAnalytics, action: string) {
  return JSON.stringify({
    category: analytics.category,
    action,
    label: analyticsLabel(analytics),
  })
}

export function PromotionalBanner({
  banner,
  rounded = true,
  customPrimaryAction,
  surfaceClassName,
  surfaceStyle,
  actionVisual,
  telemetry = DEFAULT_TELEMETRY,
}: PromotionalBannerProps) {
  const {analytics, primaryAction, actionMenu} = banner
  const primaryHref = primaryAction.href ? safeHref(primaryAction.href) : undefined
  const menuItems = useMemo(() => actionMenu?.items.filter(item => safeHref(item.href)) ?? [], [actionMenu])
  const droppedActionCount =
    (primaryHref || customPrimaryAction ? 0 : 1) + (actionMenu ? actionMenu.items.length - menuItems.length : 0)

  const telemetryTags = useMemo<IpmMetricTags>(
    () => ({
      package: 'in-product-targeting',
      component: telemetry.component,
      surface: 'banner',
      display_location: analytics.location,
      category: analytics.category,
      nudge_id: banner.nudgeId ?? analytics.category,
      variant: telemetry.variant,
    }),
    [analytics.category, analytics.location, banner.nudgeId, telemetry.component, telemetry.variant],
  )

  const primaryButton = (
    <LinkButton
      href={primaryHref ?? ''}
      target={primaryAction.openInNewTab ? '_blank' : undefined}
      rel={primaryAction.openInNewTab ? 'noopener noreferrer' : undefined}
      data-analytics-event={analyticsEvent(analytics, primaryAction.analyticsAction)}
      onClick={() =>
        reportIpmAction('cta_click', {...telemetryTags, variant: primaryAction.variant ?? telemetry.variant})
      }
    >
      {primaryAction.label}
    </LinkButton>
  )

  // Dropping an action silently would leave a campaign with a missing banner and no impressions to
  // explain it, so a rejected href is reported the same way a swallowed render failure is.
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- reports a condition found in the payload, not a user action
    if (droppedActionCount === 0) return
    sendIpmMetric({
      name: 'in_product_messaging.component.error',
      value: droppedActionCount,
      tags: {...telemetryTags, variant: 'unsafe_href'},
      samplingProbability: 1,
      defer: false,
    })
  }, [droppedActionCount, telemetryTags])

  // A banner whose only action is unusable would still record an impression, so render nothing.
  if (!primaryHref && !customPrimaryAction) return null

  return (
    <IpmBannerBase
      dismissPath={banner.dismissPath}
      visibleEvent={analyticsEvent(analytics, 'visible')}
      dismissEvent={analyticsEvent(analytics, 'dismiss')}
      testId={banner.testSelector}
      title={banner.label ? `${banner.title}, ${banner.label}` : banner.title}
      location={analytics.location}
      rounded={rounded}
      hideLeadingVisual
      className={[styles.Surface, surfaceClassName].filter(Boolean).join(' ')}
      style={surfaceStyle}
      telemetryTags={telemetryTags}
      description={
        <span className={styles.Content}>
          <span className={styles.Heading} aria-hidden="true">
            <span className={styles.Title}>{banner.title}</span>
            {banner.label ? (
              <Label variant="success" className={styles.Label}>
                {banner.label}
              </Label>
            ) : null}
          </span>
          <span className={styles.Description}>{banner.description}</span>
        </span>
      }
      primaryAction={
        customPrimaryAction ? (
          customPrimaryAction(telemetryTags)
        ) : actionMenu && menuItems.length > 0 ? (
          <ButtonGroup className={styles.ActionGroup}>
            {primaryButton}
            <ActionMenu
              onOpenChange={open => {
                // The trigger also closes an open menu, so a declarative data-analytics-event here
                // would record an "opened" action on the way back out too.
                if (!open) return
                reportIpmAction('click', {...telemetryTags, variant: actionMenu.variant ?? telemetry.variant})
                sendEvent('analytics.click', {
                  category: analytics.category,
                  action: actionMenu.analyticsAction,
                  label: analyticsLabel(analytics),
                })
              }}
            >
              <ActionMenu.Anchor>
                <IconButton icon={TriangleDownIcon} aria-label={actionMenu.ariaLabel} />
              </ActionMenu.Anchor>
              <ActionMenu.Overlay align="end">
                <ActionList>
                  {menuItems.map(item => {
                    const visual = actionVisual?.(item)
                    return (
                      <ActionList.LinkItem
                        key={item.id}
                        href={item.href}
                        target={item.openInNewTab ? '_blank' : undefined}
                        rel={item.openInNewTab ? 'noopener noreferrer' : undefined}
                        data-testid={item.id}
                        data-analytics-event={analyticsEvent(analytics, item.analyticsAction)}
                        onClick={() =>
                          reportIpmAction('cta_click', {...telemetryTags, variant: item.variant ?? telemetry.variant})
                        }
                      >
                        {visual ? <ActionList.LeadingVisual>{visual}</ActionList.LeadingVisual> : null}
                        {item.label}
                      </ActionList.LinkItem>
                    )
                  })}
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          </ButtonGroup>
        ) : (
          primaryButton
        )
      }
    />
  )
}
